import { intersection } from 'lodash';

import type {
  ContentApiImage,
  Polygon,
  Tag,
  TagInformation,
} from '@libs/trip-planner-interfaces';
import { assertValue, isDefined, renderQuery } from '@libs/utils';
import type { QueryString } from '@libs/utils';

const imagePlaceholder: ContentApiImage = {
  imageService: 'missing',
  imageName: 'missing',
};

export function getImageUrl(
  image: ContentApiImage = imagePlaceholder,
  hotelCode?: string,
  width?: number,
  height?: number
): string {
  if (image.imageName.includes('novasol')) {
    let imageUrl = image.imageName;
    if (hotelCode) {
      imageUrl = `${image.imageName}?hotelCode=${hotelCode}`;
    }
    return imageUrl;
  }

  const basePathImage =
    'https://images.sembo.travel/ImageService/ImageHandler.ashx?';

  const query: QueryString = {
    service: [image.imageService],
    nameOfImage: [image.imageName],
    width: [width?.toString() ?? '600'],
    height: [height?.toString() ?? '600'],
  };

  if (hotelCode) {
    query.hotelCode = [hotelCode];
  }
  const queryString = renderQuery(query);

  return basePathImage + queryString;
}

export function getTags<
  StaticInfoWithTags extends { tags: Tag[] },
  ResponseWithTagInformation extends { tagInformation: TagInformation[] }
>(
  hotel: StaticInfoWithTags,
  fromResponse: ResponseWithTagInformation
): string[] {
  return hotel.tags
    .map((tag) => getIconUrl(tag, fromResponse))
    .filter(
      (iconUrl): iconUrl is string => isDefined(iconUrl) && iconUrl !== ''
    );
}

function getIconUrl<
  ResponseWithTagInformation extends { tagInformation: TagInformation[] }
>(hotelTag: Tag, fromResponse: ResponseWithTagInformation): string | undefined {
  const tagInformation = fromResponse.tagInformation.find(
    (tagFromResponse) => tagFromResponse.tag === hotelTag.name
  );

  return tagInformation?.icon?.imageUrl;
}

export function getPolygons<
  StaticInfoWithPolygons extends { polygonIds: number[] },
  ResponseWithPolygons extends { polygons: Polygon[] }
>(
  hotel: StaticInfoWithPolygons,
  fromResponse: ResponseWithPolygons
): Polygon[] {
  const allPolygons = fromResponse.polygons;

  const allPolygonsById = allPolygons.reduce<Record<string, Polygon>>(
    (acc, polygon) => {
      acc[polygon.polygonId] = polygon;
      return acc;
    },
    {}
  );

  const allPolygonIds = Object.keys(allPolygonsById).map((id) =>
    parseInt(id, 10)
  );

  return intersection(hotel.polygonIds, allPolygonIds).map((id) => {
    const polygons = allPolygonsById[id];
    assertValue(polygons, 'hotels.ts > getPolygons > polygons');
    return polygons;
  });
}
